import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AsYouTypeFormatter, PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { Country, COUNTRIES } from './countries.data';

@Component({
  selector: 'lib-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    }
  ]
})
export class PhoneInputComponent implements ControlValueAccessor, OnInit {
  @Input() label = 'Phone Number';
  @Input() required = false;

  readonly allCountries = COUNTRIES;
  readonly searchCtrl = new FormControl('');

  filteredCountries: Country[] = COUNTRIES;
  selectedCountry: Country = COUNTRIES.find((c) => c.isoCode === 'US')!;
  displayValue = '';
  isDisabled = false;

  private readonly phoneUtil = PhoneNumberUtil.getInstance();
  private onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  /**
   * The numeric country calling code for the selected country (e.g. 1 for the US
   * and all NANP territories). This differs from `dialCode`, which for NANP
   * territories includes the area code (e.g. American Samoa is '1684').
   */
  private get callingCode(): number {
    return this.phoneUtil.getCountryCodeForRegion(this.selectedCountry.isoCode);
  }

  /**
   * The area-code portion of `dialCode` that is part of the national number
   * rather than the calling code. Empty for most countries; '684' for American
   * Samoa, '264' for Anguilla, etc. (NANP territories sharing calling code 1).
   */
  private get areaCode(): string {
    const cc = String(this.callingCode);
    return this.selectedCountry.dialCode.startsWith(cc)
      ? this.selectedCountry.dialCode.slice(cc.length)
      : '';
  }

  ngOnInit(): void {
    this.searchCtrl.valueChanges.subscribe((text) => {
      this.filterCountries(text ?? '');
    });
  }

  onMenuOpened(): void {
    this.searchCtrl.setValue('', { emitEvent: false });
    this.filteredCountries = this.allCountries;
    setTimeout(() => {
      document.querySelector<HTMLInputElement>('.country-search-input')?.focus();
    }, 50);
  }

  onCountrySelected(country: Country): void {
    this.selectedCountry = country;
    // Reformat the existing digits with the new country
    const digits = this.displayValue.replace(/\D/g, '');
    this.displayValue = this.formatAsYouType(digits);
    this.emitValue(digits);
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cursorPos = input.selectionStart ?? input.value.length;
    const rawDigits = this.stripEmbeddedDialCode(input.value.replace(/\D/g, ''));
    const formatted = this.formatAsYouType(rawDigits);
    this.displayValue = formatted;

    // Restore cursor: count digits before cursor in original, find same position in formatted
    const digitsBeforeCursor = input.value.slice(0, cursorPos).replace(/\D/g, '').length;
    requestAnimationFrame(() => {
      let digitCount = 0;
      let newCursor = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
          digitCount++;
          if (digitCount === digitsBeforeCursor) {
            newCursor = i + 1;
            break;
          }
        }
      }
      if (digitCount < digitsBeforeCursor) {
        newCursor = formatted.length;
      }
      input.setSelectionRange(newCursor, newCursor);
    });

    this.emitValue(rawDigits);
  }

  private filterCountries(query: string): void {
    const q = query.toLowerCase();
    this.filteredCountries = q
      ? this.allCountries.filter(
          (c) =>
            c.name.toLowerCase().includes(q) ||
            c.nativeName.toLowerCase().includes(q) ||
            c.dialCode.includes(q) ||
            c.isoCode.toLowerCase().includes(q)
        )
      : this.allCountries;
  }

  /**
   * Strips an embedded dial code from pasted digits. For NANP territories the
   * dial code includes an area code (e.g. American Samoa '1684') that is already
   * shown in the dial-code button, so pasting a full international number such as
   * '+1684...' should not leave the area code in the input. Only strips when the
   * dial code is longer than the calling code (i.e. it embeds an area code).
   */
  private stripEmbeddedDialCode(digits: string): string {
    const dialCode = this.selectedCountry.dialCode;
    return dialCode.length > String(this.callingCode).length && digits.startsWith(dialCode)
      ? digits.slice(dialCode.length)
      : digits;
  }

  private formatAsYouType(digits: string): string {
    if (!digits) return '';
    const formatter = new AsYouTypeFormatter(this.selectedCountry.isoCode);
    let result = '';
    for (const digit of digits) {
      result = formatter.inputDigit(digit);
    }
    // AsYouTypeFormatter may prepend country code for international — strip it
    const dialPrefix = `+${this.callingCode} `;
    if (result.startsWith(dialPrefix)) {
      result = result.slice(dialPrefix.length);
    }
    return result;
  }

  private emitValue(digits: string): void {
    if (!digits) {
      this.onChange('');
      return;
    }
    // For NANP territories, the selected dial code includes an area code (e.g.
    // American Samoa '1684'). Prepend it to form the full national number unless
    // the user already typed it, so the stored E.164 encodes the correct region.
    const area = this.areaCode;
    const national = area && !digits.startsWith(area) ? `${area}${digits}` : digits;
    const e164 = `+${this.callingCode}${national}`;
    try {
      const parsed = this.phoneUtil.parseAndKeepRawInput(e164, this.selectedCountry.isoCode);

      if (this.phoneUtil.isValidNumber(parsed)) {
        const format =
          this.selectedCountry.isoCode === 'US'
            ? PhoneNumberFormat.NATIONAL
            : PhoneNumberFormat.INTERNATIONAL;

        this.onChange(this.phoneUtil.format(parsed, format));
      } else {
        this.onChange(e164);
      }
    } catch {
      this.onChange(e164);
    }
  }

  writeValue(value: string): void {
    if (!value) {
      this.displayValue = '';
      return;
    }
    try {
      const parsed = this.phoneUtil.parseAndKeepRawInput(value, this.selectedCountry.isoCode);
      const regionCode = this.phoneUtil.getRegionCodeForNumber(parsed);
      if (regionCode) {
        const country = this.allCountries.find((c) => c.isoCode === regionCode);
        if (country) {
          this.selectedCountry = country;
        }
      }
      // Format national digits through AsYouTypeFormatter, stripping the selected
      // country's area code (e.g. '684') so it isn't shown twice — it is already
      // represented by the dial-code button.
      const nationalDigits = parsed.getNationalNumber()?.toString() ?? '';
      const area = this.areaCode;
      const local =
        area && nationalDigits.startsWith(area)
          ? nationalDigits.slice(area.length)
          : nationalDigits;
      this.displayValue = this.formatAsYouType(local);
    } catch {
      // If parsing fails, strip to digits and display as-is
      const digits = value.replace(/\D/g, '');
      this.displayValue = this.formatAsYouType(digits);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
}
